import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import usePlans from '../../hooks/usePlans';

import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Chip,
  Paper,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  WhatsApp,
  Facebook,
  Instagram,
  Chat,
  Speed,
  Group,
  TrendingUp,
  Star,
  CheckCircle,
  Phone,
  Email,
  LocationOn,
  Menu,
  Close,
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    overflowX: 'hidden',
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  
  // Header Styles
  header: {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    position: 'fixed',
    top: 0,
    zIndex: 1100,
  },
  
  toolbar: {
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2),
  },
  
  logo: {
    height: 40,
    marginRight: theme.spacing(2),
  },
  
  navButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  
  navButton: {
    color: 'white',
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  
  mobileMenuButton: {
    display: 'none',
    color: 'white',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  
  // Hero Section
  heroSection: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(8),
    position: 'relative',
  },
  
  heroContent: {
    textAlign: 'center',
    color: 'white',
    zIndex: 2,
  },
  
  heroTitle: {
    fontSize: '3.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    background: 'linear-gradient(45deg, #fff 30%, #f0f0f0 90%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    [theme.breakpoints.down('md')]: {
      fontSize: '2.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  
  heroSubtitle: {
    fontSize: '1.5rem',
    marginBottom: theme.spacing(4),
    opacity: 0.9,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
    },
  },
  
  heroButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  
  primaryButton: {
    background: 'linear-gradient(45deg, #25D366 30%, #128C7E 90%)',
    color: 'white',
    padding: theme.spacing(1.5, 4),
    fontSize: '1.1rem',
    fontWeight: 600,
    borderRadius: 30,
    textTransform: 'none',
    boxShadow: '0 8px 32px rgba(37, 211, 102, 0.3)',
    '&:hover': {
      background: 'linear-gradient(45deg, #128C7E 30%, #25D366 90%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 40px rgba(37, 211, 102, 0.4)',
    },
    transition: 'all 0.3s ease',
  },
  
  secondaryButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    padding: theme.spacing(1.5, 4),
    fontSize: '1.1rem',
    fontWeight: 600,
    borderRadius: 30,
    textTransform: 'none',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.2)',
      transform: 'translateY(-2px)',
    },
    transition: 'all 0.3s ease',
  },
  
  // Features Section
  featuresSection: {
    padding: theme.spacing(8, 0),
    background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f8f9fa',
  },
  
  sectionTitle: {
    textAlign: 'center',
    marginBottom: theme.spacing(6),
    fontSize: '2.5rem',
    fontWeight: 700,
    color: theme.palette.mode === 'dark' ? '#fff' : '#333',
  },
  
  featureCard: {
    height: '100%',
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)'
      : 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
    borderRadius: 20,
    padding: theme.spacing(3),
    textAlign: 'center',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: theme.palette.mode === 'dark' 
        ? '0 20px 40px rgba(255,255,255,0.1)'
        : '0 20px 40px rgba(0,0,0,0.1)',
    },
  },
  
  featureIcon: {
    fontSize: 60,
    color: '#25D366',
    marginBottom: theme.spacing(2),
  },
  
  featureTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.mode === 'dark' ? '#fff' : '#333',
  },
  
  featureDescription: {
    color: theme.palette.mode === 'dark' ? '#ccc' : '#666',
    lineHeight: 1.6,
  },
  
  // Stats Section
  statsSection: {
    padding: theme.spacing(8, 0),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
  
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  
  statNumber: {
    fontSize: '3rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  
  statLabel: {
    fontSize: '1.2rem',
    opacity: 0.9,
  },
  
  // Testimonials Section
  testimonialsSection: {
    padding: theme.spacing(8, 0),
    background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f8f9fa',
  },
  
  testimonialCard: {
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)'
      : 'white',
    borderRadius: 20,
    padding: theme.spacing(4),
    height: '100%',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: theme.palette.mode === 'dark' 
        ? '0 15px 30px rgba(255,255,255,0.1)'
        : '0 15px 30px rgba(0,0,0,0.1)',
    },
  },
  
  planCard: {
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(145deg, #2a2a2a 0%, #1e1e1e 100%)'
      : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: 24,
    padding: 0,
    height: '100%',
    maxWidth: 320,
    margin: '0 auto',
    border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #25D366 100%)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '&:hover': {
      transform: 'translateY(-12px) scale(1.02)',
      [theme.breakpoints.down('md')]: {
        transform: 'translateY(-8px) scale(1.01)',
      },
      [theme.breakpoints.down('sm')]: {
        transform: 'translateY(-4px) scale(1.01)',
      },
      boxShadow: theme.palette.mode === 'dark' 
        ? '0 25px 50px rgba(102, 126, 234, 0.15), 0 10px 20px rgba(255,255,255,0.05)'
        : '0 25px 50px rgba(102, 126, 234, 0.15), 0 10px 20px rgba(0,0,0,0.1)',
      border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
      '&::before': {
        opacity: 1,
      },
    },
  },
  
  planCardPopular: {
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(145deg, #2a2a2a 0%, #1e1e1e 100%)'
      : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: 24,
    padding: 0,
    height: '100%',
    border: `3px solid ${theme.palette.primary.main}`,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    transform: 'scale(1.05)',
    [theme.breakpoints.down('md')]: {
      transform: 'scale(1.02)',
    },
    [theme.breakpoints.down('sm')]: {
      transform: 'scale(1)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #25D366 100%)`,
      opacity: 1,
    },
    '&:hover': {
      transform: 'translateY(-12px) scale(1.08)',
      [theme.breakpoints.down('md')]: {
        transform: 'translateY(-8px) scale(1.04)',
      },
      [theme.breakpoints.down('sm')]: {
        transform: 'translateY(-4px) scale(1.02)',
      },
      boxShadow: theme.palette.mode === 'dark' 
        ? `0 30px 60px rgba(102, 126, 234, 0.25), 0 15px 30px rgba(255,255,255,0.08)`
        : `0 30px 60px rgba(102, 126, 234, 0.25), 0 15px 30px rgba(0,0,0,0.15)`,
    },
  },
  
  planCardContent: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2.5),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  
  planHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    position: 'relative',
  },
  
  planName: {
    fontSize: '1.5rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.4rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.3rem',
    },
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)'
      : 'linear-gradient(135deg, #333 0%, #666 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  
  planPrice: {
    fontSize: '2.8rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '2.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.2rem',
    },
    fontWeight: 800,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1,
    marginBottom: theme.spacing(0.5),
  },
  
  planRecurrence: {
    fontSize: '1rem',
    color: theme.palette.mode === 'dark' ? '#aaa' : '#666',
    fontWeight: 500,
  },
  
  planFeatures: {
    flexGrow: 1,
    marginBottom: theme.spacing(2),
  },
  
  planFeature: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.3, 0),
    borderRadius: 8,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255,255,255,0.03)'
        : 'rgba(102, 126, 234, 0.05)',
      transform: 'translateX(3px)',
    },
  },
  
  planFeatureIcon: {
    color: '#25D366',
    marginRight: theme.spacing(1.5),
    fontSize: '1.2rem',
  },
  
  planFeatureText: {
    fontSize: '0.95rem',
    color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#555',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  
  planButton: {
    borderRadius: 16,
    padding: theme.spacing(1.5, 3),
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      transition: 'left 0.5s ease',
    },
    '&:hover::before': {
      left: '100%',
    },
  },
  
  popularBadge: {
    position: 'absolute',
    top: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: theme.spacing(0.5, 2),
    borderRadius: 20,
    fontSize: '0.85rem',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    zIndex: 1,
  },
  
  plansContainer: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
    },
  },
  
  testimonialText: {
    fontSize: '1.1rem',
    fontStyle: 'italic',
    marginBottom: theme.spacing(3),
    color: theme.palette.mode === 'dark' ? '#ccc' : '#555',
    lineHeight: 1.6,
  },
  
  testimonialAuthor: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  
  authorAvatar: {
    width: 50,
    height: 50,
    background: 'linear-gradient(45deg, #25D366 30%, #128C7E 90%)',
  },
  
  authorInfo: {
    flex: 1,
  },
  
  authorName: {
    fontWeight: 600,
    color: theme.palette.mode === 'dark' ? '#fff' : '#333',
  },
  
  authorCompany: {
    color: theme.palette.mode === 'dark' ? '#ccc' : '#666',
    fontSize: '0.9rem',
  },
  
  // Footer
  footer: {
    background: theme.palette.mode === 'dark' ? '#0d0d0d' : '#2c3e50',
    color: 'white',
    padding: theme.spacing(6, 0, 3, 0),
  },
  
  footerContent: {
    marginBottom: theme.spacing(4),
  },
  
  footerSection: {
    marginBottom: theme.spacing(4),
  },
  
  footerTitle: {
    fontSize: '1.3rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: '#25D366',
  },
  
  footerLink: {
    color: '#ccc',
    textDecoration: 'none',
    display: 'block',
    marginBottom: theme.spacing(1),
    '&:hover': {
      color: '#25D366',
    },
    transition: 'color 0.3s ease',
  },
  
  footerBottom: {
    borderTop: '1px solid rgba(255,255,255,0.1)',
    paddingTop: theme.spacing(3),
    textAlign: 'center',
    color: '#ccc',
  },
  
  socialIcons: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  
  socialIcon: {
    color: '#ccc',
    fontSize: 30,
    '&:hover': {
      color: '#25D366',
    },
    transition: 'color 0.3s ease',
    cursor: 'pointer',
  },
}));

const LandingPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const { getPlanList, list } = usePlans();
  
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      console.log('🚀 Iniciando fetchPlans...');
      setLoadingPlans(true);
      console.log('⏳ Loading plans set to true');
      
      try {
        console.log('📡 Intentando obtener planes públicos desde la API...');
        let planList = await getPlanList({ listPublic: true });
        console.log('✅ Public plans fetched from API:', planList);
        
        // Si no hay planes públicos, intentar obtener todos los planes
        if (!planList || planList.length === 0) {
          console.log('📡 No hay planes públicos, intentando obtener todos los planes...');
          try {
            planList = await list();
            console.log('✅ All plans fetched from API:', planList);
          } catch (allPlansError) {
            console.log('❌ Error fetching all plans:', allPlansError);
          }
        }
        
        if (planList && planList.length > 0) {
          setPlans(planList);
          console.log('🎯 Using real plans from API');
        } else {
          console.log('⚠️ No plans found, using fallback');
          // Fallback solo si no hay planes
          const fallbackPlans = [
            {
              id: 1,
              name: "Básico",
              amount: "29.99",
              recurrence: "monthly",
              users: 3,
              connections: 1,
              queues: 3,
              useWhatsapp: true,
              useFacebook: false,
              useInstagram: false,
              useCampaigns: false,
              useSchedules: true,
              useInternalChat: true,
              useExternalApi: false,
              isPublic: true
            },
            {
              id: 2,
              name: "Profesional",
              amount: "59.99",
              recurrence: "monthly",
              users: 10,
              connections: 3,
              queues: 10,
              useWhatsapp: true,
              useFacebook: true,
              useInstagram: true,
              useCampaigns: true,
              useSchedules: true,
              useInternalChat: true,
              useExternalApi: false,
              isPublic: true
            },
            {
              id: 3,
              name: "Empresarial",
              amount: "99.99",
              recurrence: "monthly",
              users: 50,
              connections: 10,
              queues: 50,
              useWhatsapp: true,
              useFacebook: true,
              useInstagram: true,
              useCampaigns: true,
              useSchedules: true,
              useInternalChat: true,
              useExternalApi: true,
              isPublic: true
            }
          ];
          setPlans(fallbackPlans);
        }
      } catch (error) {
        console.error('❌ Error fetching plans:', error);
        console.log('🔄 Using fallback plans due to error');
        // Fallback en caso de error
        const fallbackPlans = [
          {
            id: 1,
            name: "Básico",
            amount: "29.99",
            recurrence: "monthly",
            users: 3,
            connections: 1,
            queues: 3,
            useWhatsapp: true,
            useFacebook: false,
            useInstagram: false,
            useCampaigns: false,
            useSchedules: true,
            useInternalChat: true,
            useExternalApi: false,
            isPublic: true
          },
          {
            id: 2,
            name: "Profesional",
            amount: "59.99",
            recurrence: "monthly",
            users: 10,
            connections: 3,
            queues: 10,
            useWhatsapp: true,
            useFacebook: true,
            useInstagram: true,
            useCampaigns: true,
            useSchedules: true,
            useInternalChat: true,
            useExternalApi: false,
            isPublic: true
          },
          {
            id: 3,
            name: "Empresarial",
            amount: "99.99",
            recurrence: "monthly",
            users: 50,
            connections: 10,
            queues: 50,
            useWhatsapp: true,
            useFacebook: true,
            useInstagram: true,
            useCampaigns: true,
            useSchedules: true,
            useInternalChat: true,
            useExternalApi: true,
            isPublic: true
          }
        ];
        setPlans(fallbackPlans);
      } finally {
        console.log('🏁 Setting loading to false');
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  const handleLogin = () => {
    history.push('/login');
  };

  const handleSignup = () => {
    history.push('/signup');
  };

  const handlePlans = () => {
    // Scroll to plans section
    const plansSection = document.getElementById('plans-section');
    if (plansSection) {
      plansSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: <WhatsApp className={classes.featureIcon} />,
      title: 'WhatsApp Business',
      description: 'Gestiona todos tus números de WhatsApp Business, API o QR, en un solo lugar y responde conversaciones de forma efectiva.',
    },
    {
      icon: <Facebook className={classes.featureIcon} />,
      title: 'Facebook Messenger',
      description: 'Visualiza todos tus comentarios y mensajes en una misma bandeja, y responde automáticamente con nuestro chatbot.',
    },
    {
      icon: <Instagram className={classes.featureIcon} />,
      title: 'Instagram Direct',
      description: 'Asegúrate de que cada prospecto cuente. Da seguimiento a todos tus mensajes de Instagram y cierra ventas.',
    },
    {
      icon: <Chat className={classes.featureIcon} />,
      title: 'Web Chat',
      description: 'Implementa un canal de soporte en tu página web y gestiona consultas en tiempo real.',
    },
    {
      icon: <Speed className={classes.featureIcon} />,
      title: 'Chatbot Inteligente',
      description: 'Envía de forma automática mensajes predefinidos y proporciona respuestas rápidas las 24 horas del día.',
    },
    {
      icon: <Group className={classes.featureIcon} />,
      title: 'Gestión de Equipos',
      description: 'Gestiona la comunicación de tu equipo con herramientas de asignación automática y estadísticas de desempeño.',
    },
  ];

  const stats = [
    { number: '4.8/5', label: 'Calificación CSAT' },
    { number: '+4,000', label: 'Empresas Activas' },
    { number: '+2M', label: 'Conversaciones Automatizadas' },
    { number: '5x', label: 'Velocidad de Respuesta' },
  ];

  const testimonials = [
    {
      text: 'Descubrimos Whaticket y quedamos encantados con su facilidad de uso, rápida implementación y su precio accesible. Actualmente, Whaticket ha resuelto nuestros problemas de gestión de citas y recordatorios.',
      author: 'Xavier L.',
      company: 'BIODIMED',
    },
    {
      text: 'Desde que comenzamos a usar Whaticket, hemos logrado resolver el problema de comunicación entre vendedores y clientes de manera eficiente.',
      author: 'Ludmila J.',
      company: 'BIMAT',
    },
    {
      text: 'En Caribe Sur Store, nos encontramos con el desafío de unificar nuestras diversas plataformas. Tras explorar diversas soluciones, descubrimos Whaticket, una herramienta increíblemente amigable y fácil de usar.',
      author: 'Sergio M.',
      company: 'Caribe Sur Store',
    },
  ];

  return (
    <Box className={classes.root}>
      {/* Header */}
      <AppBar className={classes.header} elevation={0}>
        <Toolbar className={classes.toolbar}>
          <Box display="flex" alignItems="center">
            <img 
              src="/logo.png" 
              alt="Logo" 
              className={classes.logo}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <Typography variant="h6" style={{ color: 'white', fontWeight: 700 }}>
              Cloud CRM PR
            </Typography>
          </Box>
          
          <Box className={classes.navButtons}>
            <Button 
              className={classes.navButton}
              onClick={handleLogin}
            >
              Iniciar Sesión
            </Button>
            <Button 
              className={classes.navButton}
              onClick={handleSignup}
            >
              Registrarse
            </Button>
            <Button 
              className={classes.navButton}
              onClick={handlePlans}
            >
              Planes
            </Button>
          </Box>
          
          <IconButton className={classes.mobileMenuButton}>
            <Menu />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box className={classes.heroSection}>
        <Container maxWidth="lg">
          <Box className={classes.heroContent}>
            <Typography variant="h1" className={classes.heroTitle}>
              Plataforma de automatización multiagente de chats
            </Typography>
            <Typography variant="h4" className={classes.heroSubtitle}>
              Conecta WhatsApp, Facebook, Instagram y más canales en una sola plataforma. Automatiza respuestas, gestiona equipos y aumenta tus ventas.
            </Typography>
            <Box className={classes.heroButtons}>
              <Button 
                className={classes.primaryButton}
                onClick={handleSignup}
                startIcon={<WhatsApp />}
              >
                Comenzar Prueba Gratis
              </Button>
              <Button 
                className={classes.secondaryButton}
                onClick={handleLogin}
              >
                Iniciar Sesión
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box className={classes.featuresSection}>
        <Container maxWidth="lg">
          <Typography variant="h2" className={classes.sectionTitle}>
            ¿Qué hacemos?
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            style={{ 
              marginBottom: theme.spacing(6), 
              color: theme.palette.mode === 'dark' ? '#ccc' : '#666',
              maxWidth: 800,
              margin: '0 auto',
              marginBottom: theme.spacing(6),
            }}
          >
            Centraliza, automatiza y gestiona todos tus canales de mensajería desde una sola plataforma con nuestra herramienta multiagente.
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card className={classes.featureCard} elevation={0}>
                  <CardContent>
                    {feature.icon}
                    <Typography variant="h5" className={classes.featureTitle}>
                      {feature.title}
                    </Typography>
                    <Typography className={classes.featureDescription}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box className={classes.statsSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box className={classes.statCard}>
                  <Typography variant="h2" className={classes.statNumber}>
                    {stat.number}
                  </Typography>
                  <Typography className={classes.statLabel}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box className={classes.testimonialsSection}>
        <Container maxWidth="lg">
          <Typography variant="h2" className={classes.sectionTitle}>
            +4.000 clientes confían en Cloud CRM PR
          </Typography>
          
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card className={classes.testimonialCard} elevation={0}>
                  <CardContent>
                    <Typography className={classes.testimonialText}>
                      "{testimonial.text}"
                    </Typography>
                    <Box className={classes.testimonialAuthor}>
                      <Avatar className={classes.authorAvatar}>
                        {testimonial.author.charAt(0)}
                      </Avatar>
                      <Box className={classes.authorInfo}>
                        <Typography className={classes.authorName}>
                          {testimonial.author}
                        </Typography>
                        <Typography className={classes.authorCompany}>
                          {testimonial.company}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Plans Section */}
      <Box id="plans-section" className={classes.section}>
        <Container maxWidth="lg">
          <Typography variant="h3" className={classes.sectionTitle} align="center">
            Nuestros Planes
          </Typography>
          <Typography variant="h6" className={classes.sectionSubtitle} align="center">
            Elige el plan perfecto para tu negocio
          </Typography>
          
          {loadingPlans ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <Typography>Cargando planes...</Typography>
            </Box>
          ) : (
            <Grid container spacing={4} className={classes.plansContainer}>
              {plans.map((plan, index) => {
                const isPopular = index === 1 && plans.length >= 3;
                return (
                  <Grid item xs={12} md={plans.length === 1 ? 12 : plans.length === 2 ? 6 : 4} key={plan.id}>
                    <Card 
                      className={isPopular ? classes.planCardPopular : classes.planCard}
                      style={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {isPopular && (
                        <Box className={classes.popularBadge}>
                          ⭐ Más Popular
                        </Box>
                      )}
                      <CardContent className={classes.planCardContent}>
                        <Box className={classes.planHeader}>
                          <Typography className={classes.planName}>
                            {plan.name}
                          </Typography>
                          <Typography className={classes.planPrice}>
                            ${plan.amount}
                          </Typography>
                          <Typography className={classes.planRecurrence}>
                            /{plan.recurrence === 'monthly' ? 'mes' : plan.recurrence === 'yearly' ? 'año' : 'mes'}
                          </Typography>
                        </Box>
                        
                        <Box className={classes.planFeatures}>
                          <Box className={classes.planFeature}>
                            <CheckCircle className={classes.planFeatureIcon} />
                            <Typography className={classes.planFeatureText}>
                              {plan.users === 0 ? 'Usuarios ilimitados' : `Hasta ${plan.users} usuarios`}
                            </Typography>
                          </Box>
                          <Box className={classes.planFeature}>
                            <CheckCircle className={classes.planFeatureIcon} />
                            <Typography className={classes.planFeatureText}>
                              {plan.connections === 0 ? 'Conexiones ilimitadas' : `${plan.connections} conexiones`}
                            </Typography>
                          </Box>
                          <Box className={classes.planFeature}>
                            <CheckCircle className={classes.planFeatureIcon} />
                            <Typography className={classes.planFeatureText}>
                              {plan.queues === 0 ? 'Colas ilimitadas' : `${plan.queues} colas`}
                            </Typography>
                          </Box>
                          {plan.useWhatsapp && (
                            <Box className={classes.planFeature}>
                              <WhatsApp className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>WhatsApp Business</Typography>
                            </Box>
                          )}
                          {plan.useFacebook && (
                            <Box className={classes.planFeature}>
                              <Facebook className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>Facebook Messenger</Typography>
                            </Box>
                          )}
                          {plan.useInstagram && (
                            <Box className={classes.planFeature}>
                              <Instagram className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>Instagram Direct</Typography>
                            </Box>
                          )}
                          {plan.useCampaigns && (
                            <Box className={classes.planFeature}>
                              <TrendingUp className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>Campañas automáticas</Typography>
                            </Box>
                          )}
                          {plan.useSchedules && (
                            <Box className={classes.planFeature}>
                              <CheckCircle className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>Horarios de atención</Typography>
                            </Box>
                          )}
                          {plan.useInternalChat && (
                            <Box className={classes.planFeature}>
                              <Chat className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>Chat interno</Typography>
                            </Box>
                          )}
                          {plan.useExternalApi && (
                            <Box className={classes.planFeature}>
                              <CheckCircle className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>API externa</Typography>
                            </Box>
                          )}
                          {plan.useKanban && (
                            <Box className={classes.planFeature}>
                              <CheckCircle className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>Vista Kanban</Typography>
                            </Box>
                          )}
                          {plan.useOpenAi && (
                            <Box className={classes.planFeature}>
                              <Star className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>Integración OpenAI</Typography>
                            </Box>
                          )}
                          {plan.useIntegrations && (
                            <Box className={classes.planFeature}>
                              <CheckCircle className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>Integraciones avanzadas</Typography>
                            </Box>
                          )}
                          {plan.trial && (
                            <Box className={classes.planFeature}>
                              <Star className={classes.planFeatureIcon} />
                              <Typography className={classes.planFeatureText}>
                                Prueba gratuita de {plan.trialDays} días
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        
                        <Button
                          variant={isPopular ? "contained" : "outlined"}
                          color="primary"
                          fullWidth
                          className={classes.planButton}
                          onClick={handleSignup}
                        >
                          {plan.trial ? 'Comenzar Prueba Gratuita' : 'Comenzar'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {plans.length === 0 && !loadingPlans && (
                <Grid item xs={12}>
                  <Typography align="center" color="textSecondary">
                    No hay planes disponibles en este momento.
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Footer */}
      <Box className={classes.footer}>
        <Container maxWidth="lg">
          <Grid container spacing={4} className={classes.footerContent}>
            <Grid item xs={12} md={3}>
              <Box className={classes.footerSection}>
                <Typography className={classes.footerTitle}>
                  Cloud CRM PR
                </Typography>
                <Typography style={{ color: '#ccc', marginBottom: theme.spacing(2) }}>
                  Plataforma de automatización multiagente para gestión de chats y atención al cliente.
                </Typography>
                <Box className={classes.socialIcons}>
                  <WhatsApp className={classes.socialIcon} />
                  <Facebook className={classes.socialIcon} />
                  <Instagram className={classes.socialIcon} />
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box className={classes.footerSection}>
                <Typography className={classes.footerTitle}>
                  Producto
                </Typography>
                <Link href="#" className={classes.footerLink}>Características</Link>
                <Link href="#" className={classes.footerLink}>Integraciones</Link>
                <Link href="#" className={classes.footerLink}>API</Link>
                <Link href="#" className={classes.footerLink}>Seguridad</Link>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box className={classes.footerSection}>
                <Typography className={classes.footerTitle}>
                  Soporte
                </Typography>
                <Link href="#" className={classes.footerLink}>Centro de Ayuda</Link>
                <Link href="#" className={classes.footerLink}>Documentación</Link>
                <Link href="#" className={classes.footerLink}>Contacto</Link>
                <Link href="#" className={classes.footerLink}>Estado del Servicio</Link>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box className={classes.footerSection}>
                <Typography className={classes.footerTitle}>
                  Empresa
                </Typography>
                <Link href="#" className={classes.footerLink}>Acerca de</Link>
                <Link href="#" className={classes.footerLink}>Blog</Link>
                <Link href="#" className={classes.footerLink}>Carreras</Link>
                <Link href="#" className={classes.footerLink}>Términos de Servicio</Link>
                <Link href="#" className={classes.footerLink}>Política de Privacidad</Link>
              </Box>
            </Grid>
          </Grid>
          
          <Box className={classes.footerBottom}>
            <Typography>
              © 2025 Cloud CRM PR. Todos los derechos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;